import React, { useState } from 'react';
import { 
  Database, 
  Users, 
  FolderOpen, 
  Trash2, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle,
  Play,
  Square
} from 'lucide-react';
import { TestDataGenerator } from '../../utils/testDataGenerator';

export const TestDataManager: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [lastAction, setLastAction] = useState<'generate' | 'clear' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()} - ${message}`]);
  };

  const handleGenerateData = async () => {
    setIsGenerating(true);
    setError(null);
    setLogs([]);
    
    try {
      addLog('Début de la génération des données de test...');
      
      // Override console.log to capture logs
      const originalLog = console.log;
      console.log = (...args) => {
        addLog(args.join(' '));
        originalLog(...args);
      };

      await TestDataGenerator.generateAllTestData();
      
      // Restore console.log
      console.log = originalLog;
      
      setLastAction('generate');
      addLog('✅ Génération terminée avec succès !');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la génération des données');
      addLog(`❌ Erreur: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClearData = async () => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer toutes les données de test ? Cette action est irréversible.')) {
      return;
    }

    setIsClearing(true);
    setError(null);
    setLogs([]);
    
    try {
      addLog('Début de la suppression des données de test...');
      
      const originalLog = console.log;
      console.log = (...args) => {
        addLog(args.join(' '));
        originalLog(...args);
      };

      await TestDataGenerator.clearAllTestData();
      
      console.log = originalLog;
      
      setLastAction('clear');
      addLog('✅ Suppression terminée avec succès !');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression des données');
      addLog(`❌ Erreur: ${err.message}`);
    } finally {
      setIsClearing(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3 mb-2">
          <Database className="w-8 h-8" />
          <h1 className="text-2xl font-bold">Gestionnaire de données de test</h1>
        </div>
        <p className="text-blue-100">
          Créez ou supprimez des données de test pour développer et tester l'application
        </p>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={handleGenerateData}
          disabled={isGenerating || isClearing}
          className="flex flex-col items-center p-6 bg-white border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
        >
          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            {isGenerating ? (
              <RefreshCw className="w-6 h-6 text-white animate-spin" />
            ) : (
              <Play className="w-6 h-6 text-white" />
            )}
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Vraies Données CRESS</h3>
          <p className="text-sm text-gray-600 text-center">
            Créer les vraies données avec projets ESS réels
          </p>
        </button>

        <button
          onClick={handleClearData}
          disabled={isGenerating || isClearing}
          className="flex flex-col items-center p-6 bg-white border border-gray-200 rounded-xl hover:bg-red-50 hover:border-red-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
        >
          <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            {isClearing ? (
              <RefreshCw className="w-6 h-6 text-white animate-spin" />
            ) : (
              <Square className="w-6 h-6 text-white" />
            )}
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Supprimer les données</h3>
          <p className="text-sm text-gray-600 text-center">
            Nettoyer toutes les données de test
          </p>
        </button>

        <button
          onClick={handleRefreshPage}
          className="flex flex-col items-center p-6 bg-white border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 group"
        >
          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <RefreshCw className="w-6 h-6 text-white" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Actualiser</h3>
          <p className="text-sm text-gray-600 text-center">
            Recharger l'application
          </p>
        </button>
      </div>

      {/* Status */}
      {lastAction && (
        <div className={`p-4 rounded-lg border ${
          lastAction === 'generate' 
            ? 'bg-green-50 border-green-200' 
            : 'bg-orange-50 border-orange-200'
        }`}>
          <div className="flex items-center space-x-2">
            {lastAction === 'generate' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            )}
            <p className={`font-medium ${
              lastAction === 'generate' ? 'text-green-800' : 'text-orange-800'
            }`}>
              {lastAction === 'generate' 
                ? 'Données de test générées avec succès !' 
                : 'Données de test supprimées avec succès !'
              }
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-800 font-medium">Erreur</p>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
        </div>
      )}

      {/* Logs */}
      {logs.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Journal d'exécution</h3>
          </div>
          <div className="p-4 max-h-64 overflow-y-auto">
            <div className="space-y-1 font-mono text-sm">
              {logs.map((log, index) => (
                <div key={index} className={`${
                  log.includes('✅') ? 'text-green-600' :
                  log.includes('❌') ? 'text-red-600' :
                  log.includes('🔄') ? 'text-blue-600' :
                  'text-gray-600'
                }`}>
                  {log}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Test Accounts Info */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-4">🏢 Comptes CRESS Guadeloupe créés</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Users className="w-4 h-4 text-red-600" />
                <span className="font-medium text-red-800">Directrice CRESS</span>
              </div>
              <p className="text-sm text-red-700">direction@cress-guadeloupe.org</p>
              <p className="text-xs text-red-600">Marie-France CHARLES - Administration</p>
            </div>

            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Users className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-purple-800">Coordinateur CRESS</span>
              </div>
              <p className="text-sm text-purple-700">coordination@cress-guadeloupe.org</p>
              <p className="text-xs text-purple-600">Jean-Claude MARTIN - Coordination</p>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-800">Accompagnatrice CRESS</span>
              </div>
              <p className="text-sm text-blue-700">accompagnement@cress-guadeloupe.org</p>
              <p className="text-xs text-blue-600">Sophie BERNARD - Accompagnement</p>
            </div>

            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Users className="w-4 h-4 text-green-600" />
                <span className="font-medium text-green-800">Porteur Agriculture</span>
              </div>
              <p className="text-sm text-green-700">marie.joseph@cooperative-gwada.gp</p>
              <p className="text-xs text-green-600">Marie-Claire JOSEPH - Coopérative Bio</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Users className="w-4 h-4 text-teal-600" />
                <span className="font-medium text-teal-800">Porteur Environnement</span>
              </div>
              <p className="text-sm text-teal-700">jean.moreau@recyclerie-gwada.org</p>
              <p className="text-xs text-teal-600">Jean-Baptiste MOREAU - Recyclerie</p>
            </div>

            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Users className="w-4 h-4 text-indigo-600" />
                <span className="font-medium text-indigo-800">Porteur Formation</span>
              </div>
              <p className="text-sm text-indigo-700">lucie.moreau@formation-numerique.gp</p>
              <p className="text-xs text-indigo-600">Lucie MOREAU - Formation Numérique</p>
            </div>

            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Users className="w-4 h-4 text-orange-600" />
                <span className="font-medium text-orange-800">Financeur Région</span>
              </div>
              <p className="text-sm text-orange-700">ess@region-guadeloupe.fr</p>
              <p className="text-xs text-orange-600">Paul ALEXANDRE - Financement ESS</p>
            </div>

            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm font-medium text-gray-800">🔑 Mots de passe sécurisés</span>
              </div>
              <p className="text-sm text-gray-700">Chaque compte a un mot de passe unique</p>
              <p className="text-xs text-gray-600">Ex: CressDirection2024!, CoopAgri2024!, etc.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Projects Preview */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-4">🏢 Projets ESS Guadeloupe créés</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <FolderOpen className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium">Coopérative Agricole Bio Gwada</span>
              <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">EN_COURS</span>
              <span className="text-xs text-gray-500">150K€</span>
            </div>
            <div className="flex items-center space-x-2">
              <FolderOpen className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium">Recyclerie Solidaire Guadeloupe</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">VALIDE</span>
              <span className="text-xs text-gray-500">85K€</span>
            </div>
            <div className="flex items-center space-x-2">
              <FolderOpen className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium">Centre Formation Numérique</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">SOUMIS</span>
              <span className="text-xs text-gray-500">120K€</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <FolderOpen className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium">Épicerie Sociale Mobile</span>
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">ACCORD_PROVISOIRE</span>
              <span className="text-xs text-gray-500">65K€</span>
            </div>
            <div className="flex items-center space-x-2">
              <FolderOpen className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium">Atelier Réparation Participatif</span>
              <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">EN_COURS</span>
              <span className="text-xs text-gray-500">40K€</span>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="font-medium text-green-800">💰 Financement total : 460 000€</span>
          </div>
          <div className="text-xs text-green-700 mt-1">
            5 projets ESS avec accompagnateurs assignés et documents complets
          </div>
        </div>
      </div>

      {/* Real Accounts List */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-4">👥 Comptes créés avec projets</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm">🌱 PORTEURS DE PROJET</h4>
            
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-medium text-green-800">Marie-Claire JOSEPH</p>
              <p className="text-sm text-green-700">marie.joseph@cooperative-gwada.gp</p>
              <p className="text-xs text-green-600">Coopérative Agricole Bio (150K€)</p>
            </div>
            
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <p className="font-medium text-teal-800">Jean-Baptiste MOREAU</p>
              <p className="text-sm text-teal-700">jean.moreau@recyclerie-gwada.org</p>
              <p className="text-xs text-teal-600">Recyclerie Solidaire (85K€)</p>
            </div>
            
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="font-medium text-indigo-800">Lucie MOREAU</p>
              <p className="text-sm text-indigo-700">lucie.moreau@formation-numerique.gp</p>
              <p className="text-xs text-indigo-600">Formation Numérique (120K€)</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm">🤝 ACCOMPAGNATEURS</h4>
            
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="font-medium text-blue-800">Sophie BERNARD</p>
              <p className="text-sm text-blue-700">accompagnement@cress-guadeloupe.org</p>
              <p className="text-xs text-blue-600">CRESS Guadeloupe</p>
            </div>
            
            <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
              <p className="font-medium text-cyan-800">Patricia LEBLANC</p>
              <p className="text-sm text-cyan-700">contact@adeig.org</p>
              <p className="text-xs text-cyan-600">ADEIG</p>
            </div>
            
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="font-medium text-emerald-800">Pierre DUBOIS</p>
              <p className="text-sm text-emerald-700">direction@pfil-guadeloupe.org</p>
              <p className="text-xs text-emerald-600">PFIL Guadeloupe</p>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-blue-900 mb-3">Instructions d'utilisation</h3>
        <div className="space-y-2 text-sm text-blue-800">
          <p>1. <strong>Cliquer "Vraies données CRESS"</strong> : Crée l'équipe CRESS + porteurs + projets réels</p>
          <p>2. <strong>Vérifier dans Supabase</strong> : Allez dans Authentication &gt; Users pour voir les comptes</p>
          <p>3. <strong>Se connecter</strong> : Utilisez les emails ci-dessus avec leurs mots de passe</p>
          <p>4. <strong>Actualiser</strong> : Rechargez la page pour voir les changements dans l'interface</p>
          <p>5. <strong>Tester les profils</strong> : Chaque porteur voit ses projets, chaque accompagnateur son portefeuille</p>
        </div>
      </div>
    </div>
  );
};